'use client';

import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentClientIc } from '../../../../components/icons';
import { BillsTableProps, RevshareBill } from '../types';
import { getTableColumns } from '../constants';
import IdCell from './IdCell';
import AccountCell from './AccountCell';
import StatusCell from './StatusCell';
import DateCell from './DateCell';

const BillsTable: React.FC<BillsTableProps> = ({
  bills,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onPayBill,
}) => {
  const { t, lang } = useTranslations();

  // Helper to get numeric amount
  const getAmount = (amount: string | number): number => {
    if (typeof amount === 'string') {
      return parseFloat(amount) || 0;
    }
    return amount;
  };

  const renderCell = useCallback(
    (bill: RevshareBill, columnKey: string) => {
      switch (columnKey) {
        case 'id':
          return <IdCell bill={bill} />;
        case 'account':
          return <AccountCell bill={bill} />;
        case 'amount':
          return (
            <Typography variant="body2" className="font-semibold text-auth-ink">
              ${getAmount(bill.amount).toFixed(2)}
            </Typography>
          );
        case 'status':
          return <StatusCell bill={bill} onPayBill={onPayBill} />;
        case 'date':
          return <DateCell bill={bill} />;
        default:
          return null;
      }
    },
    [onPayBill]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-primary',
            circle2: '!border-b-primary/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      key={lang}
      data={bills}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('client.revshare.bills.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('client.revshare.bills.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default BillsTable;
